import ApplyV2QuestionsList from '@core/components/ApplyDialogV2/ApplyV2QuestionsStep/ApplyV2QuestionsList'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import parseDateStringToObject from '@core/utils/helpers/parseDateStringToObject'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import { FormikProvider, useFormik } from 'formik'
import React, { useEffect } from 'react'
import {
    ApplyV2CustomFieldTypeConst,
    type ApplyV2FileAttachment,
    type ApplyV2Question,
    type ApplyV2SettingsSectionName,
    type ApplyV2TempCourseFile,
    type GetApplyV2SettingsResponse,
} from '@core/app/types/applyV2Type'
import { useGetAllQuestionsV2ApplyQuery, useValidateQuestionsV2ApplyMutation } from '@core/app/api/applyJobV2Api'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import * as Yup from 'yup'
import './ApplyV2QuestionsStep.scss'

const baseClass = 'ApplyV2QuestionsStep'

function ApplyV2QuestionsStep() {
    const { storageData } = useApplyV2Context()

    const { data, isLoading, isFetching, refetch } = useGetAllQuestionsV2ApplyQuery(
        { uid: storageData?.postInitUid || '' },
        { skip: !storageData?.postInitUid }
    )

    useEffect(() => {
        if (refetch) refetch()
    }, [])

    if (isLoading || isFetching || !storageData?.postInitUid) {
        return (
            <div className={`${baseClass}__container`}>
                <ApplySkeleton style={{ width: '100%', height: '100px' }} isLoading={true} />
                <ApplySkeleton style={{ width: '100%', height: '50px' }} isLoading={true} />
                <ApplySkeleton style={{ width: '100%', height: '50px' }} isLoading={true} />
                <div style={{ width: '100%', display: 'flex', justifyContent: 'end' }}>
                    <ApplySkeleton style={{ width: '80px', height: '40px' }} isLoading={true} />
                </div>
            </div>
        )
    }

    const questions = [...(data?.data?.questions || [])].sort((a, b) => a.orderId - b.orderId)

    // console.log(questions)

    return (
        <div className={baseClass}>
            <ApplyV2QuestionsForm questions={questions} />
        </div>
    )
}
interface FormValues {
    [key: string]:
        | string
        | number
        | boolean
        | null
        | undefined
        | number[]
        | string[]
        | [string | null, string | null]
        | { id: number; rating: number | null }[]
        | (File | ApplyV2FileAttachment | ApplyV2TempCourseFile)[]
}

interface ApplyV2QuestionsFormProps {
    // children: React.ReactNode
    questions: ApplyV2Question[]
}

function ApplyV2QuestionsForm({ questions }: ApplyV2QuestionsFormProps) {
    const { getTitle } = useApplyV2NavTranslations()
    const { storageData, onNextStep } = useApplyV2Context()
    const [validateRequest, { isLoading: isValidateLoading }] = useValidateQuestionsV2ApplyMutation()
    const buildInitialValues = (questions: ApplyV2Question[], parent?: ApplyV2Question | null): Record<string, any> => {
        return questions.reduce(
            (acc, question) => {
                const questionKey = `${question.type}_${question.questionId}`

                if (question.type === 'customField' && 'customField' in question) {
                    switch (question.customField?.typeId) {
                        case ApplyV2CustomFieldTypeConst.MultiSelect:
                            acc[questionKey] = question.answer?.value || []
                            break
                        case ApplyV2CustomFieldTypeConst.SingleSelect:
                            acc[questionKey] = question.answer?.value ?? null
                            break
                        case ApplyV2CustomFieldTypeConst.Date:
                            const dateValue = typeof question.answer?.value === 'string' ? question.answer.value : null
                            const { day, month, year } = parseDateStringToObject(dateValue)
                            acc[`day_date_${questionKey}`] = day
                            acc[`month_date_${questionKey}`] = month
                            acc[`year_date_${questionKey}`] = year
                            break
                        case ApplyV2CustomFieldTypeConst.Integer:
                            // const decimals = question.customField?.properties?.decimals ?? 0
                            const decimals = question.customField?.properties?.decimals
                            // const val = Number(question.answer?.value ?? 0).toFixed(decimals)
                            const val = question.answer?.value
                                ? Number(question.answer?.value ?? 0).toFixed(decimals)
                                : null
                            acc[questionKey] = val
                            break
                        case ApplyV2CustomFieldTypeConst.Text:
                            acc[questionKey] = question.answer?.value ?? null
                            break
                        default:
                            acc[questionKey] = question.answer?.value ?? ''
                    }
                } else if (question.type === 'attribute') {
                    if (question.attribute.bool) {
                        acc[`bool_${questionKey}`] = question.answer?.value?.bool ?? null
                    }
                    if (question.attribute.checkbox) {
                        const iShowCheckboxes = question.attribute.checkbox
                        const checkedAnswer = Array.isArray(question.answer?.value?.checkbox)
                            ? question?.answer?.value.checkbox
                            : []
                        acc[`checkboxes_${questionKey}`] = (question.attribute.checkboxes ?? []).map((item) => {
                            const answerItem = (Array.isArray(checkedAnswer) ? checkedAnswer : []).find(
                                (t) => t.id === item.id
                            )
                            return {
                                ...item,
                                checked: !!answerItem,
                                rating: answerItem?.rating ?? 0,
                            }
                        })
                        // acc[`checkboxes_${questionKey}`] =
                        //     question.answer?.value?.checkbox ??
                        //     question.attribute.checkboxes?.map((it) => ({
                        //         ...it,
                        //         checked: false,
                        //         rating: 0,
                        //     })) // validation {id: number, rating: number (1-5)} {id: number, name: string, checked: boolean, rating: 0-5}[]
                    }
                    if (question.attribute.date) {
                        const { day, month, year } = parseDateStringToObject(question.answer?.value?.date)
                        acc[`day_date_${questionKey}`] = day
                        acc[`month_date_${questionKey}`] = month
                        acc[`year_date_${questionKey}`] = year
                    }
                    if (question.attribute.shortText) {
                        acc[`shortText_${questionKey}`] = question.answer?.value?.shortText ?? '' // validation string length 0 - 1024
                    }
                    if (question.attribute.text) {
                        acc[`text_${questionKey}`] = question.answer?.value?.text ?? '' // validation string length 0 - 5000
                    }
                    if (question.attribute.rating) {
                        acc[`rating_${questionKey}`] = question.answer?.value?.rating ?? null // validation number 1-5
                    }
                    if (question.attribute.dropdown) {
                        // acc[`dropdown_${questionKey}`] = question.answer?.value?.dropdown ?? null // validation number
                        acc[`dropdowns_${questionKey}`] = question.answer?.value?.dropdown ?? null // validation number
                    }
                } else {
                    switch (question.type) {
                        case 'multiSelect':
                            acc[questionKey] = question.answer?.value || [] // validation number[]
                            break
                        case 'singleSelect':
                            acc[questionKey] = question.answer?.value || null // validation number
                            break
                        case 'date':
                            // const [year = null, month = null, day = null] = question.answer?.value?.split('-') ?? []
                            const { day, month, year } = parseDateStringToObject(question.answer?.value)
                            acc[`day_${questionKey}`] = day
                            acc[`month_${questionKey}`] = month
                            acc[`year_${questionKey}`] = year
                            break
                        // case 'dateRange':
                        //     acc[questionKey] = question.answer?.value || [null, null] // validation [string, string] like ['2022-07-03', '2023-04-02'] - always date string
                        //     break
                        case 'dateRange':
                            const [startDate = null, endDate = null] = question.answer?.value || [null, null]
                            const {
                                year: startYear,
                                month: startMonth,
                                day: startDay,
                            } = parseDateStringToObject(startDate)
                            acc[`startDay_${questionKey}`] = startDay
                            acc[`startMonth_${questionKey}`] = startMonth
                            acc[`startYear_${questionKey}`] = startYear

                            const { year: endYear, month: endMonth, day: endDay } = parseDateStringToObject(endDate)
                            acc[`endDay_${questionKey}`] = endDay
                            acc[`endMonth_${questionKey}`] = endMonth
                            acc[`endYear_${questionKey}`] = endYear

                            // acc[questionKey] = [startDate, endDate]
                            break
                        case 'number':
                            acc[questionKey] = question.answer?.value ?? null //validation number
                            break
                        case 'starRating':
                            acc[questionKey] = question.answer?.value ?? null //validation number 1 - 5
                            break

                        case 'attachment':
                            // console.log(
                            //     question.answer?.value,
                            //     Array.isArray(question.answer?.value),
                            //     typeof question.answer?.value
                            // )
                            acc[questionKey] = Array.isArray(question.answer?.value) ? question.answer?.value : [] // validation {name: string | max length 128, ext: string, base64: string}
                            break
                        case 'yesNo':
                            acc[questionKey] = question.answer?.value ?? null //validation boolean
                            break
                        case 'qualifying':
                            acc[questionKey] = question.answer?.value ?? null //validation boolean
                            break
                        case 'text':
                            acc[questionKey] = question.answer?.value || '' // validation string length 0 - 1024
                            break
                        case 'longText':
                            acc[questionKey] = question.answer?.value || '' // validation string length 0 - 5000
                            break
                        default:
                            // @ts-ignore
                            acc[questionKey] = question.answer?.value ?? ''
                    }
                }

                if (question.children?.length) {
                    Object.assign(acc, buildInitialValues(question.children, question))
                }

                return acc
            },
            {} as Record<string, any>
        )
    }

    const buildValidationSchema = (questions: ApplyV2Question[], parent?: ApplyV2Question): Yup.ObjectSchema<any> => {
        const shape = questions.reduce(
            (acc, question) => {
                const questionKey = `${question.type}_${question.questionId}`
                const parentKey = parent ? `${parent.type}_${parent.questionId}` : null

                if (question.type === 'customField' && 'customField' in question) {
                    switch (question.customField?.typeId) {
                        case ApplyV2CustomFieldTypeConst.MultiSelect:
                            const parentValue = Array.isArray(parent?.answer?.value) ? parent?.answer?.value : []
                            acc[questionKey] = Yup.array().of(Yup.number()).nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey]
                                    .required(getTitle('3924', 'Required filed'))
                                    .min(1, 'Please select at least one option')
                            }
                            break
                        case ApplyV2CustomFieldTypeConst.SingleSelect:
                            acc[questionKey] = Yup.number().nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey].required(getTitle('3924', 'Required filed'))
                            }
                            break
                        case ApplyV2CustomFieldTypeConst.Date:
                            // acc[questionKey] = Yup.string()
                            //     .nullable()
                            //     .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid Date')
                            acc[`year_date_${questionKey}`] = Yup.string().nullable()
                            acc[`month_date_${questionKey}`] = Yup.string().nullable()
                            acc[`day_date_${questionKey}`] = Yup.string().nullable()
                            if (question.required) {
                                // acc[questionKey] = acc[questionKey].required('This field is required')
                                acc[`year_date_${questionKey}`] = acc[`year_date_${questionKey}`].required(
                                    getTitle('6722', 'Year is required')
                                )
                                acc[`month_date_${questionKey}`] = acc[`month_date_${questionKey}`].required(
                                    getTitle('6721', 'Month is required')
                                )
                                acc[`day_date_${questionKey}`] = acc[`day_date_${questionKey}`].required(
                                    getTitle('10310', 'Day is required')
                                )
                            }
                            break
                        case ApplyV2CustomFieldTypeConst.Integer:
                            const { decimals = 0, min, max } = question.customField?.properties || {}

                            let fieldValidation = Yup.string()
                                .nullable()
                                .test('is-numeric', 'Must be a valid number', (value) => {
                                    if (value === null || value === undefined || value === '') return true
                                    return !isNaN(Number(value.replace(',', '.')))
                                })

                            // if (decimals > 0) {
                            //     fieldValidation = fieldValidation.test(
                            //         'decimal-precision',
                            //         `Must have exactly ${decimals} decimal place${decimals !== 1 ? 's' : ''}`,
                            //         (value) => {
                            //             if (value === null || value === undefined || value === '')
                            //                 return !question.required
                            //
                            //             const normalizedValue = value.toString().replace(',', '.')
                            //
                            //             if (!normalizedValue.includes('.')) return false
                            //
                            //             const parts = normalizedValue.split('.')
                            //             if (parts.length !== 2) return false
                            //
                            //             return parts[1].length === decimals
                            //         }
                            //     )
                            // }

                            if (typeof min === 'number') {
                                fieldValidation = fieldValidation.test(
                                    'min-value',
                                    `${getTitle('9763', 'Minimum')} ${min}`,
                                    (value) => {
                                        if (value === null || value === undefined || value === '') return true
                                        const numericValue = Number(value.replace(',', '.'))
                                        return !isNaN(numericValue) && numericValue >= min
                                    }
                                )
                            }
                            if (typeof max === 'number') {
                                fieldValidation = fieldValidation.test(
                                    'max-value',
                                    `${getTitle('9764', 'Maximum')} ${max}`,
                                    (value) => {
                                        if (value === null || value === undefined || value === '') return true
                                        const numericValue = Number(value.replace(',', '.'))
                                        return !isNaN(numericValue) && numericValue <= max
                                    }
                                )
                            }

                            if (question.required) {
                                fieldValidation = fieldValidation.required(getTitle('3924', 'Required filed'))
                            }

                            acc[questionKey] = fieldValidation
                            break
                        default:
                            acc[questionKey] = Yup.string().max(1024, 'Maximum length 1024 characters').nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey].required(getTitle('3924', 'Required filed'))
                            }
                    }
                } else if (question.type === 'attribute') {
                    if (question.attribute.bool) {
                        const key = `bool_${questionKey}`
                        acc[key] = Yup.boolean().nullable()
                        if (question.required) {
                            acc[key] = acc[key].required(getTitle('3924', 'Required filed'))
                        }
                    }
                    if (question.attribute.checkbox) {
                        const key = `checkboxes_${questionKey}`
                        acc[key] = Yup.array()
                            .of(
                                Yup.object().shape({
                                    id: Yup.number().required(),
                                    rating: Yup.number().min(0).max(5).nullable(),
                                    checked: Yup.boolean(),
                                })
                            )
                            .nullable()
                            .test('at-least-one-checked', 'At least one item must be checked', function (value) {
                                if (!value) return false
                                return value.some((item) => item.checked === true)
                            })
                            .required(getTitle('3924', 'Required filed'))
                    }
                    if (question.attribute.date) {
                        // const key = `date_${questionKey}`
                        // acc[key] = Yup.string()
                        //     .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid Date')
                        //     .nullable()
                        //     .required('Attribute date required')
                        acc[`year_date_${questionKey}`] = Yup.string().nullable()
                        acc[`month_date_${questionKey}`] = Yup.string().nullable()
                        acc[`day_date_${questionKey}`] = Yup.string().nullable()

                        if (question.required) {
                            acc[`year_date_${questionKey}`] = acc[`year_date_${questionKey}`].required(
                                getTitle('6722', 'Year is required')
                            )
                            acc[`month_date_${questionKey}`] = acc[`month_date_${questionKey}`].required(
                                getTitle('6721', 'Month is required')
                            )
                            acc[`day_date_${questionKey}`] = acc[`day_date_${questionKey}`].required(
                                getTitle('10310', 'Day is required')
                            )
                        }
                    }
                    if (question.attribute.shortText) {
                        const key = `shortText_${questionKey}`
                        acc[key] = Yup.string().max(1024, 'Maximum length 1024 characters').nullable()
                        if (question.required) {
                            acc[key] = acc[key].required('Short text required')
                        }
                    }
                    if (question.attribute.text) {
                        const key = `text_${questionKey}`
                        acc[key] = Yup.string().max(5000, 'Maximum length 5000 characters').nullable()
                        if (question.required) {
                            acc[key] = acc[key].required(getTitle('3924', 'Required filed'))
                        }
                    }
                    if (question.attribute.rating) {
                        const key = `rating_${questionKey}`
                        acc[key] = Yup.number().nullable()
                        if (question.required) {
                            acc[key] = acc[key].min(1).max(5).required(getTitle('3924', 'Required filed'))
                        }
                    }
                    if (question.attribute.dropdown) {
                        // const dropKey = `dropdown_${questionKey}`
                        const dropdownsKey = `dropdowns_${questionKey}`
                        // acc[dropdownsKey] = Yup.array().of(Yup.number())
                        // if (question.required) {
                        //     acc[dropdownsKey] = acc[dropdownsKey].min(1, 'Please select at least one option')
                        // }
                        acc[dropdownsKey] = Yup.number().nullable()
                        if (question.required) {
                            acc[dropdownsKey] = acc[dropdownsKey].required(getTitle('3924', 'Required filed'))
                        }
                    }
                } else {
                    switch (question.type) {
                        case 'multiSelect':
                            acc[questionKey] = Yup.array().of(Yup.number()).nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey]
                                    .required(getTitle('3924', 'Required filed'))
                                    .min(1, 'Please select at least one option')
                            }
                            break
                        case 'singleSelect':
                            acc[questionKey] = Yup.number().nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey].required(getTitle('3924', 'Required filed'))
                            }
                            break
                        case 'date':
                            acc[`year_${questionKey}`] = Yup.string().nullable()
                            acc[`month_${questionKey}`] = Yup.string().nullable()
                            acc[`day_${questionKey}`] = Yup.string().nullable()

                            if (question.required) {
                                acc[`year_${questionKey}`] = acc[`year_${questionKey}`].required(
                                    getTitle('6722', 'Year is required')
                                )
                                acc[`month_${questionKey}`] = acc[`month_${questionKey}`].required(
                                    getTitle('6721', 'Month is required')
                                )
                                acc[`day_${questionKey}`] = acc[`day_${questionKey}`].required(
                                    getTitle('10310', 'Day is required')
                                )
                            }
                            break
                        case 'dateRange':
                            acc[`startYear_${questionKey}`] = Yup.string().nullable()
                            acc[`startMonth_${questionKey}`] = Yup.string().nullable()
                            acc[`startDay_${questionKey}`] = Yup.string().nullable()
                            acc[`endYear_${questionKey}`] = Yup.string().nullable()
                            acc[`endMonth_${questionKey}`] = Yup.string().nullable()
                            acc[`endDay_${questionKey}`] = Yup.string().nullable()
                            if (question.required) {
                                acc[`startYear_${questionKey}`] = acc[`startYear_${questionKey}`].required(
                                    getTitle('6722', 'Year is required')
                                )
                                acc[`startMonth_${questionKey}`] = acc[`startMonth_${questionKey}`].required(
                                    getTitle('6721', 'Month is required')
                                )
                                acc[`startDay_${questionKey}`] = acc[`startDay_${questionKey}`].required(
                                    getTitle('10310', 'Day is required')
                                )
                                acc[`endYear_${questionKey}`] = acc[`endYear_${questionKey}`].required(
                                    getTitle('6722', 'Year is required')
                                )
                                acc[`endMonth_${questionKey}`] = acc[`endMonth_${questionKey}`].required(
                                    getTitle('6721', 'Month is required')
                                )
                                acc[`endDay_${questionKey}`] = acc[`endDay_${questionKey}`].required(
                                    getTitle('10310', 'Day is required')
                                )
                            }
                            break
                        case 'number':
                            acc[questionKey] = Yup.number().nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey].required(getTitle('3924', 'Required filed'))
                            }
                            break
                        case 'starRating':
                            acc[questionKey] = Yup.number().min(1).max(5).nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey].required(getTitle('3924', 'Required filed'))
                            }
                            break
                        case 'attachment':
                            // acc[questionKey] = Yup.object()
                            //     .shape({
                            //         name: Yup.string().max(128).required('This field is required'),
                            //         ext: Yup.string().required(),
                            //         base64: Yup.string().required(),
                            //     })
                            //     .nullable()
                            // if (question.required) {
                            //     acc[questionKey] = acc[questionKey].required('This field is required')
                            // }
                            acc[questionKey] = Yup.array()
                                .of(
                                    Yup.object().shape({
                                        name: Yup.string().max(128).required('File name is required'),
                                        ext: Yup.string().required('File extension is required'),
                                        // base64: Yup.string().required('File content is required'),
                                        size: Yup.number().required('File size is required'),
                                    })
                                )
                                .test('required', getTitle('3924', 'Required filed'), function (value) {
                                    if (question.required) {
                                        return !!value && value.length > 0
                                    }
                                    return true
                                })
                            break
                        case 'yesNo':
                            acc[questionKey] = Yup.boolean().nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey].required(getTitle('3924', 'Required filed'))
                            }
                            break
                        case 'qualifying':
                            acc[questionKey] = Yup.boolean().nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey].required(getTitle('3924', 'Required filed'))
                            }
                            break
                        case 'text':
                            acc[questionKey] = Yup.string().max(1024, 'Maximum length 1024 characters').nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey].required(getTitle('3924', 'Required filed'))
                            }
                            break
                        case 'longText':
                            acc[questionKey] = Yup.string().max(5000, 'Maximum length 5000 characters').nullable()
                            if (question.required) {
                                acc[questionKey] = acc[questionKey].required(getTitle('3924', 'Required filed'))
                            }
                            break
                        default:
                            acc[questionKey] = Yup.mixed()
                    }
                }

                if (question.children?.length) {
                    const childSchema = buildValidationSchema(question.children, question)
                    Object.assign(acc, childSchema.fields)
                }

                return acc
            },
            {} as Record<string, any>
        )

        return Yup.object().shape(shape)
    }

    const validationSchema = buildValidationSchema(questions)

    const formik = useFormik<FormValues>({
        initialValues: buildInitialValues(questions),
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            validateRequest({
                uid: storageData?.postInitUid || '',
            })
                .unwrap()
                .then((res) => {
                    if (res.data?.isValid) {
                        onNextStep()
                    }
                })
        },
        enableReinitialize: true,
    })

    console.log(questions)

    return (
        <FormikProvider value={formik}>
            <form className={`${baseClass}__container`} onSubmit={formik.handleSubmit}>
                <ApplyV2QuestionsList
                    questions={questions}
                    parent={{
                        parentValue: null,
                        parentType: null,
                        parentQuestionId: null,
                        parentOptionId: null,
                    }}
                />
                <div className={`${baseClass}__next`}>
                    <ApplyV2Button
                        type="submit"
                        variant={'primary'}
                        className={`${baseClass}__next--btn`}
                        isLoading={isValidateLoading}
                        isDisabled={isValidateLoading}
                        // onClick={() => {
                        //     console.log(formik.values, formik.errors)
                        // }}
                    >
                        {getTitle('3035', 'Next')}
                    </ApplyV2Button>
                </div>
            </form>
        </FormikProvider>
    )
}

export default ApplyV2QuestionsStep
